'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';
import { openChat, openCall } from '@/components/integrations/Kodif/Actions';
import { QuestionMarkCircleIcon as QuestionIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import CTAButton from '@/components/elements/CTAButton';
import {
  DEFAULT_TIME_ZONE,
  getTimesText,
  getTodaysSchedule,
  isScheduleUnavailable,
} from '@/utils/cxSchedule';

const renderRteParagraphs = (rte) => {
  if (!rte?.children) return null;
  return rte.children
    .filter((node) => node.type === 'p')
    .map((p, i) => (
      <p key={i} className={i > 0 ? 'mt-3' : ''}>
        {(p.children || []).map((leaf) => leaf.text || '').join('')}
      </p>
    ));
};

const FaqAccordionItem = ({ question, answer, icon }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-warm-white">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-4 py-4 text-left cursor-pointer"
      >
        <div className="w-8 flex items-center shrink-0">
          {icon?.url && (
            <Image
              src={icon.url}
              alt=""
              width={30}
              height={30}
              className="w-full object-contain h-[30px]"
            />
          )}
        </div>
        <div className="w-full flex gap-3 justify-between items-center">
          <span className="font-worksans text-primary text-sm font-semibold">
            {question}
          </span>
          <ArrowDownIcon
            aria-hidden="true"
            className={`size-4 text-sea shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 font-worksans text-primary text-sm leading-relaxed">
          {renderRteParagraphs(answer)}
        </div>
      )}
    </div>
  );
};

const getCtaAction = (ctaItem) => {
  if (ctaItem?.type === 'chat') return openChat;
  if (ctaItem?.type === 'call') return () => openCall(ctaItem.phone);
  return undefined;
};
const ContactCX = ({ block, position }) => {
  const entry = block?.entry || block;
  const data = entry || {};

  const { settings } = useSiteSettings();
  const uid = data?.uid ?? data?._metadata?.uid ?? 'contact-cx';
  const sectionId = position != null ? `${uid}-${position}` : undefined;

  const { background_color, always_show_mobile = true } = data;
  const image = data.profile_image?.url || data.image;
  const scheduleSource = settings?.cxHours;

  const desktop_cta = data.desktop_cta || {
    heading: data.heading_one,
    subheading: data.heading_two,
    description: data.short_content,
    cta: [
      { label: data.chat_button_label, type: 'chat', _metadata: { uid: 'chat-cta' } },
      { label: data.call_button_label, type: 'call', phone: data.telephone_number, _metadata: { uid: 'call-cta' } },
    ].filter((cta) => cta.label),
  };

  const mobile_cta = data.mobile_cta || {
    heading: data.heading_one,
    label: data.chat_button_label,
    type: 'chat',
    leading_icon: 'QuestionIcon',
  };

  const [userTimeZone, setUserTimeZone] = useState(null);
  const [userLocale, setUserLocale] = useState(null);
  const isMounted = userTimeZone !== null;

  useEffect(() => {
    if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
      const resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimeZone(resolvedTimeZone || DEFAULT_TIME_ZONE);
    } else {
      setUserTimeZone(DEFAULT_TIME_ZONE);
    }
    setUserLocale(typeof navigator !== 'undefined' ? navigator.language || 'en-US' : 'en-US');
  }, []);

  const getSchedule = ({ type, label }) => {
    const tz = userTimeZone || DEFAULT_TIME_ZONE;
    const locale = userLocale || 'en-US';
    const key = (type || label || '').toLowerCase();
    const serviceType = key.includes('call') ? 'call' : 'chat';
    return getTodaysSchedule(scheduleSource, tz, serviceType, locale);
  };

  const mobileCXSchedule = getSchedule(mobile_cta);
  const isMobileCtaDisabled = !isMounted || isScheduleUnavailable(mobileCXSchedule);
  
  const IconMap = {
    QuestionIcon,
    ChatIcon: QuestionIcon,
  };
  const IconComponent = IconMap[mobile_cta?.leading_icon] || QuestionIcon;
  const renderIcon = <IconComponent className="size-6 shrink-0" />;

  if (data.visible === false) {
    return null;
  }

  return (
    <section
      id={sectionId}
      data-name="contact-cx"
      data-position={position}
      className={background_color || 'bg-white'}
    >
      {/* MOBILE */}
      {always_show_mobile && (
        <div className="flex sm:hidden items-center justify-between bg-white py-4 px-6">
          <div className="flex items-center space-x-2 text-primary font-medium">
            {renderIcon}
            <span className="leading-none whitespace-nowrap">{mobile_cta?.heading}</span>
          </div>

          <CTAButton
            variant="outline"
            disabled={isMobileCtaDisabled}
            onClick={getCtaAction(mobile_cta)}
            className="px-5 py-3 text-nowrap"
          >
            {mobile_cta?.label}
          </CTAButton>
        </div>
      )}

      {/* DESKTOP */}
      <div className={`${always_show_mobile ? 'hidden sm:flex' : 'flex'} flex-col gap-4 p-6 border-2 border-[#F1F1F1]`}>
        <div className="flex gap-2">
          {image && (
            <Image
              src={image}
              alt="Contact CX"
              width={50}
              height={50}
              className="w-[50px] h-[50px] min-w-[50px] min-h-[50px] rounded-full object-cover"
            />
          )}

          <div className="w-full flex flex-col text-base leading-6">
            {desktop_cta?.heading && (
              <div className="font-semibold text-primary">{desktop_cta.heading}</div>
            )}
            {desktop_cta?.subheading && (
              <div className="font-semibold text-primary">{desktop_cta.subheading}</div>
            )}
            {desktop_cta?.description && (
              <div className="text-primary text-sm font-normal mt-1">{desktop_cta.description}</div>
            )}

            {/* BUTTONS */}
            <div className="flex gap-4 mt-4 w-full">
              {(desktop_cta?.cta || []).map((ctaItem, index) => {
                const scheduleState = getSchedule(ctaItem);
                const { isAvailableNow } = scheduleState;
                const timesText = getTimesText(scheduleState);
                const ctaDisabled = !isMounted || isScheduleUnavailable(scheduleState);

                return (
                  <div key={ctaItem?._metadata?.uid || index} className="flex flex-col gap-2 flex-1">
                    {/* CHAT / CALL */}
                    <CTAButton
                      variant="outline"
                      disabled={ctaDisabled}
                      onClick={getCtaAction(ctaItem)}
                      className="w-full text-base leading-6 py-3"
                    >
                      {ctaItem.label}
                    </CTAButton>

                    {/* BL-409: the dot is the same claim as the text, in colour
                        form. With no hours for today it would sit there red and
                        wordless, so it goes with the text. */}
                    <div className="flex gap-1 text-xs font-normal text-primary min-h-[16px]">
                      {isMounted && scheduleState.isConfigured && (
                        <>
                          <div>
                            <div
                              className={`w-[10px] h-[10px] rounded-full ${
                                isAvailableNow ? 'bg-cx-available' : 'bg-cx-unavailable'
                              }`}
                              aria-hidden
                            />
                          </div>
                          <div className="-mt-[3px]">
                            {timesText}
                          </div>
                        </>
                      )}
                      {!isMounted && <span>{'\u00A0'}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {data.faqs?.length > 0 && (
          <div className="flex flex-col gap-1">
            {data.faqs.map((faq, index) => (
              <FaqAccordionItem
                key={faq?._metadata?.uid || index}
                question={faq?.question}
                answer={faq?.answer}
                icon={faq?.icon}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ContactCX;
